import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  IconButton,
  Avatar,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Language as WebsiteIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  AttachMoney as AttachMoneyIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Star as StarIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  School as SchoolIcon,
  EmojiEvents as AchievementIcon,
  RecordVoiceOver as SpeakingIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { GuestSpeaker } from '../../types';

interface SpeakerViewDialogProps {
  open: boolean;
  speaker: GuestSpeaker;
  onClose: () => void;
}

const SpeakerViewDialog: React.FC<SpeakerViewDialogProps> = ({
  open,
  speaker,
  onClose,
}) => {
  // const getStatusBadge = () => {
  //   if (speaker.isKeynoteSpeaker) {
  //     return (
  //       <Chip
  //         icon={<CampaignIcon sx={{ fontSize: 16 }} />}
  //         label="Keynote Speaker"
  //         sx={{
  //           bgcolor: '#fef3c7',
  //           color: '#d97706',
  //           fontWeight: 600,
  //         }}
  //       />
  //     );
  //   }
  //   if (speaker.isFeaturedSpeaker) {
  //     return (
  //       <Chip
  //         icon={<StarIcon sx={{ fontSize: 16 }} />}
  //         label="Featured Speaker"
  //         sx={{
  //           bgcolor: '#dbeafe',
  //           color: '#1d4ed8',
  //           fontWeight: 600,
  //         }}
  //       />
  //     );
  //   }
  //   return null;
  // };

  const getStatusBadge = () => {
    const badges = [];

    if (speaker.isKeynoteSpeaker) {
      badges.push(
        <Chip
          key="keynote"
          icon={<CampaignIcon sx={{ fontSize: 16 }} />}
          label="Keynote Speaker"
          sx={{
            bgcolor: '#fef3c7',
            color: '#d97706',
            fontWeight: 600,
          }}
        />
      );
    }

    if (speaker.isFeaturedSpeaker) {
      badges.push(
        <Chip
          key="featured"
          icon={<StarIcon sx={{ fontSize: 16 }} />}
          label="Featured Speaker"
          sx={{
            bgcolor: '#dbeafe',
            color: '#1d4ed8',
            fontWeight: 600,
          }}
        />
      );
    }

    return badges;
  };
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Speaker Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          <Avatar
            src={speaker.profileImageUrl}
            sx={{
              width: 100,
              height: 100,
              bgcolor: '#7c3aed',
              fontSize: '2rem',
              fontWeight: 500,
            }}
          >
            {!speaker.profileImageUrl && (
              <>
                {speaker.firstName?.charAt(0)}
                {speaker.lastName?.charAt(0)}
              </>
            )}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {speaker.title} {speaker.firstName} {speaker.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {speaker.designation}
              {speaker.organization && ` at ${speaker.organization}`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {getStatusBadge()}
              <Chip
                icon={speaker.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                label={speaker.isActive ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  bgcolor: speaker.isActive ? '#dcfce7' : '#fee2e2',
                  color: speaker.isActive ? '#16a34a' : '#dc2626',
                }}
              />
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Contact Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon sx={{ color: '#6b7280' }} />
                </ListItemIcon>
                <ListItemText primary={speaker.email} />
              </ListItem>
              {speaker.phone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon sx={{ color: '#6b7280' }} />
                  </ListItemIcon>
                  <ListItemText primary={speaker.phone} />
                </ListItem>
              )}
              {speaker.organization && (
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon sx={{ color: '#6b7280' }} />
                  </ListItemIcon>
                  <ListItemText primary={speaker.organization} />
                </ListItem>
              )}
            </List>
          </Grid>

          {/* Bio */}
          {speaker.bio && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Biography
              </Typography>
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.6, color: '#4b5563' }}
              >
                {speaker.bio}
              </Typography>
            </Grid>
          )}

          {/* Social Links */}
          {(speaker.linkedinUrl ||
            speaker.twitterUrl ||
            speaker.websiteUrl) && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Social Links
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {speaker.linkedinUrl && (
                  <Link
                    href={speaker.linkedinUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton size="small" sx={{ bgcolor: '#f3f4f6' }}>
                      <LinkedInIcon sx={{ color: '#0077b5' }} />
                    </IconButton>
                  </Link>
                )}
                {speaker.twitterUrl && (
                  <Link
                    href={speaker.twitterUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton size="small" sx={{ bgcolor: '#f3f4f6' }}>
                      <TwitterIcon sx={{ color: '#1da1f2' }} />
                    </IconButton>
                  </Link>
                )}
                {speaker.websiteUrl && (
                  <Link
                    href={speaker.websiteUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconButton size="small" sx={{ bgcolor: '#f3f4f6' }}>
                      <WebsiteIcon sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Link>
                )}
              </Box>
            </Grid>
          )}

          {/* Professional Details */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, mb: 2, fontWeight: 600 }}
            >
              Professional Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <WorkIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Experience
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 3.5 }}>
                  {speaker.yearsOfExperience || 0} years
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Expertise Areas */}
          {speaker.expertiseAreas && speaker.expertiseAreas.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <SchoolIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Expertise Areas
                </Typography>
              </Box>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 3.5 }}
              >
                {speaker.expertiseAreas.map((area, index) => (
                  <Chip
                    key={index}
                    label={area}
                    size="small"
                    sx={{
                      bgcolor: '#f3e8ff',
                      color: '#7c3aed',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Speaking Topics */}
          {speaker.speakingTopics && speaker.speakingTopics.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <SpeakingIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Speaking Topics
                </Typography>
              </Box>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 3.5 }}
              >
                {speaker.speakingTopics.map((topic, index) => (
                  <Chip
                    key={index}
                    label={topic}
                    size="small"
                    sx={{
                      bgcolor: '#e0f2fe',
                      color: '#0284c7',
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Requirements */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, mb: 2, fontWeight: 600 }}
            >
              Requirements
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlightIcon
                  sx={{ color: speaker.travelRequired ? '#16a34a' : '#9ca3af' }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: speaker.travelRequired ? '#111827' : '#9ca3af' }}
                >
                  Travel {speaker.travelRequired ? 'Required' : 'Not Required'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HotelIcon
                  sx={{
                    color: speaker.accommodationRequired
                      ? '#16a34a'
                      : '#9ca3af',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: speaker.accommodationRequired
                      ? '#111827'
                      : '#9ca3af',
                  }}
                >
                  Accommodation{' '}
                  {speaker.accommodationRequired ? 'Required' : 'Not Required'}
                </Typography>
              </Box>
            </Box>
            {speaker.specialRequirements && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Special Requirements:
                </Typography>
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {speaker.specialRequirements}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Sessions */}
          {speaker.sessions && speaker.sessions.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <ScheduleIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Assigned Sessions ({speaker.sessions.length})
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 3.5 }}
              >
                This speaker is assigned to {speaker.sessions.length} conference
                session(s).
              </Typography>
            </Grid>
          )}

          {/* Metadata */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}
            >
              <Typography variant="caption" color="text.secondary">
                Created: {formatDate(speaker.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Updated: {formatDate(speaker.updatedAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpeakerViewDialog;
